import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Section, StatusBadge, ProgressBar, Eyebrow } from "@/components/domain/Bits";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { money, num, fmtDate, RAFFLE_STATUS, raffleImg, onImgError } from "@/lib/format";
import { ShieldCheck, Calendar, Gift, Landmark, FileText, CheckCircle2, ArrowRight, Tag } from "lucide-react";

export default function RaffleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canSeeInventory = ["admin", "tenant_owner", "superadmin"].includes(user?.role);
  const [r, setR] = useState(null);
  const [img, setImg] = useState(0);
  useEffect(() => { api.get(`/raffles/${id}`).then((x) => setR(x.data)); }, [id]);
  if (!r) return <Section className="py-20"><div className="h-96 animate-pulse rounded-xl bg-slate-100" /></Section>;
  const st = RAFFLE_STATUS[r.status] || RAFFLE_STATUS.active;
  const canBuy = ["active", "live"].includes(r.status);

  return (
    <Section className="py-10">
      <div className="mb-4 text-sm text-slate-400"><Link to="/sorteos" className="hover:text-brand">Sorteos</Link> / <span className="text-slate-600">{r.name}</span></div>
      <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="aspect-[16/10] overflow-hidden"><img src={r.gallery?.[img] || raffleImg(r)} onError={onImgError} alt={r.name} className="h-full w-full object-cover" /></div>
          </div>
          {r.gallery?.length > 1 && (
            <div className="mt-3 flex gap-2">
              {r.gallery.map((g, i) => (
                <button key={i} onClick={() => setImg(i)} className={`h-16 w-24 overflow-hidden rounded-lg border-2 ${img === i ? "border-brand" : "border-transparent"}`}>
                  <img src={g} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="mt-8 space-y-6">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><Gift className="h-5 w-5 text-brand" />El premio</h2>
              <p className="mt-2 text-slate-600">{r.description}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="flex items-center gap-2 font-semibold text-slate-900"><FileText className="h-4 w-4 text-brand" />Bases y método</h3>
              <p className="mt-2 text-sm text-slate-600">{r.rules}</p>
              <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600"><ShieldCheck className="h-4 w-4 text-emerald-500" />Método: {r.method}</div>
            </div>
            <div>
              <h3 className="mb-3 font-semibold text-slate-900">Preguntas frecuentes</h3>
              <Accordion type="single" collapsible className="rounded-xl border border-slate-200 bg-white px-4">
                {[["¿Cómo sé que ganó mi boleto?", "Publicamos el número ganador y el perfil del ganador con evidencia. Puedes consultar tu folio en cualquier momento."],
                  ["¿Cómo pago?", "Por transferencia a una cuenta bancaria oficial. Recibirás un folio y datos exactos al reservar."],
                  ["¿Cuándo se confirman mis boletos?", "Después de que nuestro equipo valide tu comprobante de pago."]].map(([q, a], i) => (
                  <AccordionItem key={i} value={`i${i}`}>
                    <AccordionTrigger className="text-sm font-semibold">{q}</AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600">{a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>

        {/* Buy panel */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <StatusBadge label={st.label} cls={st.cls} />
              <span className="text-xs text-slate-400">Sorteo</span>
            </div>
            <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-900">{r.name}</h1>
            <div className="mt-1 text-sm text-slate-500">{r.prize}</div>
            <div className="mt-5 grid grid-cols-2 gap-4">
              <div><div className="text-xs text-slate-400">Precio por boleto</div><div className="font-mono text-2xl font-bold text-slate-900">{money(r.price)}</div></div>
              <div><div className="text-xs text-slate-400">Valor del premio</div><div className="font-mono text-2xl font-bold text-brand">{money(r.prize_value)}</div></div>
            </div>
            <div className="mt-5">
              <div className="mb-1 flex justify-between text-xs text-slate-500"><span>Progreso de ventas</span><span className="font-mono font-semibold text-brand">{r.progress}%</span></div>
              <ProgressBar value={r.progress} />
              {canSeeInventory && <div className="mt-2 text-xs text-slate-400" data-testid="raffle-inventory"><span className="font-mono">{num(r.available)}</span> disponibles · <span className="font-mono">{num(r.total_tickets)}</span> totales</div>}
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
              <Calendar className="h-4 w-4 text-brand" />Sorteo: {fmtDate(r.draw_date)}
            </div>
            {r.promotions?.length > 0 && (
              <div className="mt-3 space-y-2">
                {r.promotions.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
                    <Tag className="h-4 w-4" />{p.label}
                  </div>
                ))}
              </div>
            )}
            {canBuy ? (
              <Button onClick={() => nav(`/comprar/${r.id}`)} size="lg" className="mt-5 w-full rounded-full bg-brand hover:bg-brand-hover" data-testid="detail-participate">
                Participar ahora <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button disabled size="lg" className="mt-5 w-full rounded-full" data-testid="detail-disabled">{st.label}</Button>
            )}
            <div className="mt-4 space-y-2 border-t border-slate-100 pt-4 text-xs text-slate-500">
              {["Pago por transferencia a cuenta oficial", "Folio único y seguro", "Comprobante privado y validado"].map((t, i) => (
                <div key={i} className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />{t}</div>
              ))}
            </div>
          </div>
          <Link to="/transparencia" className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-3 text-sm font-medium text-slate-600 hover:text-brand">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />Verifica nuestras cuentas oficiales
          </Link>
        </div>
      </div>
    </Section>
  );
}
